/**
 * @fileoverview Dataset Template Grid — admin UI for managing migration dataset templates.
 * @module system-web/components/migration/DatasetTemplateGrid
 *
 * Responsibilities:
 * - Display all migration dataset templates in the shared GridPageShell grid.
 * - Let admins filter by status (All / Active / Draft).
 * - Let admins open the Dataset Builder wizard (New Dataset button).
 * - Let admins activate, deactivate, and delete templates via row actions.
 * - Show template details in the GridPageShell slide-in via DatasetDetailSlideIn.
 *
 * Dependencies:
 * - useDatasetTemplates hook
 * - DatasetDetailSlideIn / DatasetDetailSlideInHeader components
 * - DatasetBuilderWizard component
 * - GridPageShell, DataGridColumn, DataGridRowAction from @ui-admin
 * - lucide-react icons
 *
 * Architecture:
 * - Local-first: template state lives in Dexie, subscribed via useDatasetTemplates liveQuery.
 * - Mutations (create, update, activate, deactivate, delete) go through useDatasetTemplates.
 * - No direct API calls; no fetch in useEffect.
 *
 * Notes:
 * - No tenant / department picker — datasets are system-level, not tenant-scoped.
 * - Filter dropdown: All / Active / Draft (default: All) per §3.11.8.
 * - Row click opens the detail slide-in; Edit opens the wizard.
 *
 * @component DatasetTemplateGrid
 * Purpose:     System-level grid of all migration dataset templates.
 * Props:       (none — top-level page section)
 * Behavior:    liveQuery subscription via useDatasetTemplates() hook.
 * Data Source: local (Dexie via datasetTemplateService)
 * Constraints: no direct API calls; no fetch in useEffect
 * Performance: list rendered via GridPageShell standard paging
 *
 * @reusable
 * Usage: Rendered as the "Datasets" tab in LegacyMigrationPage.
 * Rules: stateful (selected row, filter view, wizard open state); no external props required.
 */

import React, { useMemo, useState, useCallback } from 'react';
import {
  Layers, Plus, AlertTriangle, CheckCircle2, Loader2,
  ChevronDown, Activity, Trash2, Pencil,
} from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn, DataGridRowAction } from '@ui-admin/components/DataGrid';
import { useDatasetTemplates } from '../../hooks/useDatasetTemplates';
import { DatasetDetailSlideIn, DatasetDetailSlideInHeader } from './DatasetDetailSlideIn';
import { DatasetBuilderWizard } from './DatasetBuilderWizard';
import type { MigrationDatasetTemplate } from '../../types/legacyMigrationTypes';

// ---------------------------------------------------------------------------
// Filter view type
// ---------------------------------------------------------------------------

type StatusFilterView = 'all' | 'draft' | 'active';

const filterLabels: Record<StatusFilterView, string> = {
  all: 'All Datasets',
  draft: 'Draft',
  active: 'Active',
};

// ---------------------------------------------------------------------------
// Delete confirmation modal
// ---------------------------------------------------------------------------

interface DeleteConfirmModalProps {
  template: MigrationDatasetTemplate;
  onConfirm: () => void;
  onCancel: () => void;
  isMutating: boolean;
}

function DeleteConfirmModal({ template, onConfirm, onCancel, isMutating }: DeleteConfirmModalProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full mx-4">
        <h2 className="text-base font-semibold text-gray-900 mb-2">Delete Dataset Template?</h2>
        <p className="text-sm text-gray-600 mb-5">
          <strong>{template.displayName}</strong> will be permanently removed. This cannot be undone.
          Only draft templates can be deleted.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            disabled={isMutating}
            className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isMutating}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
          >
            {isMutating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * @component DatasetTemplateGrid
 * Purpose:     Grid listing all migration dataset templates with CRUD + lifecycle actions.
 * Props:       (none)
 * Behavior:    liveQuery subscription; filter dropdown; row click slide-in; new/edit wizard.
 * Data Source: local (Dexie via useDatasetTemplates liveQuery)
 * Constraints: no direct API calls; no fetch in useEffect
 * Performance: standard GridPageShell paging; no per-row lazy fetches
 */
export function DatasetTemplateGrid() {
  const [filterView, setFilterView] = useState<StatusFilterView>('all');
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardEditId, setWizardEditId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MigrationDatasetTemplate | null>(null);

  const {
    templates,
    selectedTemplate,
    isLoading,
    isMutating,
    error,
    activate,
    deactivate,
    softDelete,
    clearError,
  } = useDatasetTemplates(filterView, selectedId);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleActivate = useCallback(async (id: string) => {
    await activate(id);
  }, [activate]);

  const handleDeactivate = useCallback(async (id: string) => {
    await deactivate(id);
  }, [deactivate]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    const ok = await softDelete(deleteTarget._id);
    if (ok) {
      setDeleteTarget(null);
      if (selectedId === deleteTarget._id) setSelectedId(null);
    }
  }, [softDelete, deleteTarget, selectedId]);

  const handleOpenWizardNew = useCallback(() => {
    setWizardEditId(null);
    setWizardOpen(true);
  }, []);

  const handleOpenWizardEdit = useCallback((id: string) => {
    setWizardEditId(id);
    setWizardOpen(true);
  }, []);

  const handleWizardClose = useCallback(() => {
    setWizardOpen(false);
    setWizardEditId(null);
  }, []);

  // ---------------------------------------------------------------------------
  // Grid columns — §3.11.8 spec: Name | Status | Kind | Source Tables | Target Collections | AI Sampled | Last Updated
  // ---------------------------------------------------------------------------

  const columns = useMemo<DataGridColumn<MigrationDatasetTemplate>[]>(() => [
    {
      id: 'displayName',
      header: 'Name',
      accessorKey: 'displayName',
      sortable: true,
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      sortable: true,
      cell: ({ row }: any) => {
        const t = row.original as MigrationDatasetTemplate;
        return t.status === 'active' ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
            <Activity className="h-2.5 w-2.5" />
            Active
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
            Draft
          </span>
        );
      },
    },
    {
      id: 'kind',
      header: 'Kind',
      accessorFn: (row) => (row.kind === 'system-known' ? 'System' : 'Custom'),
      sortable: true,
      cell: ({ row }: any) => {
        const t = row.original as MigrationDatasetTemplate;
        return t.kind === 'system-known' ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
            System
          </span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
            Custom
          </span>
        );
      },
    },
    {
      id: 'sourceTables',
      header: 'Source Tables',
      accessorFn: (row) => 1 + (row.source.childTables?.length ?? 0),
      sortable: true,
    },
    {
      id: 'targetCollections',
      header: 'Target Collections',
      accessorFn: (row) => row.target.collections?.length ?? 0,
      sortable: true,
    },
    {
      id: 'aiSampled',
      header: 'AI Sampled',
      accessorFn: (row) => (row.aiMetadata ? 'Yes' : 'No'),
      sortable: true,
      cell: ({ row }: any) => {
        const t = row.original as MigrationDatasetTemplate;
        return t.aiMetadata ? (
          <span className="inline-flex items-center gap-1 text-xs text-green-700">
            <CheckCircle2 className="h-3 w-3" />
            Yes
          </span>
        ) : (
          <span className="text-xs text-gray-400">No</span>
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorFn: (row) => new Date(row.updatedAt).toLocaleDateString(),
      sortable: true,
    },
  ], []);

  // ---------------------------------------------------------------------------
  // Row actions
  // ---------------------------------------------------------------------------

  const rowActions = useMemo<DataGridRowAction<MigrationDatasetTemplate>[]>(() => [
    {
      id: 'edit',
      label: 'Edit',
      icon: Pencil,
      variant: 'default' as const,
      onClick: (row) => handleOpenWizardEdit(row._id),
      hidden: (row) => row.status === 'active',
    },
    {
      id: 'activate',
      label: 'Activate',
      icon: CheckCircle2,
      variant: 'default' as const,
      onClick: (row) => void handleActivate(row._id),
      hidden: (row) => row.status === 'active',
      disabled: () => isMutating,
    },
    {
      id: 'deactivate',
      label: 'Deactivate',
      icon: Activity,
      variant: 'default' as const,
      onClick: (row) => void handleDeactivate(row._id),
      hidden: (row) => row.status === 'draft',
      disabled: () => isMutating,
    },
    {
      id: 'delete',
      label: 'Delete',
      icon: Trash2,
      variant: 'danger' as const,
      onClick: (row) => setDeleteTarget(row),
      hidden: (row) => row.status === 'active',
      disabled: () => isMutating,
    },
  ], [handleActivate, handleDeactivate, handleOpenWizardEdit, isMutating]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="h-full min-h-0 flex flex-col">
      {error && (
        <div className="mx-6 mt-3 flex items-center gap-2 text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2 shrink-0">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          <span>{error}</span>
          <button onClick={clearError} className="ml-auto text-red-400 hover:text-red-600 text-xs">Dismiss</button>
        </div>
      )}

      <GridPageShell<MigrationDatasetTemplate>
        gridId="legacy-migration-dataset-templates"
        gridType="legacy-migration-dataset-templates"
        title="Dataset Templates"
        subtitle={`${templates.length} template${templates.length !== 1 ? 's' : ''}`}
        columns={columns}
        data={templates}
        getRowId={(row) => row._id}
        rowActions={rowActions}
        onRowClick={(row) => setSelectedId(row._id)}
        activeRowId={selectedId}
        searchPlaceholder="Search dataset templates…"
        defaultPageSize={50}
        toolbarLeftExtra={
          <div className="flex items-center gap-2">
            <div className="relative">
              <button
                onClick={() => setFilterDropdownOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-gray-300 rounded-lg bg-white text-gray-700 hover:bg-gray-50 transition-colors"
              >
                {filterLabels[filterView]}
                <ChevronDown className="h-3 w-3" />
              </button>
              {filterDropdownOpen && (
                <div className="absolute left-0 top-full mt-1 w-36 rounded-lg border border-gray-200 bg-white shadow-lg z-20">
                  {(['all', 'active', 'draft'] as StatusFilterView[]).map((view) => (
                    <button
                      key={view}
                      onClick={() => { setFilterView(view); setFilterDropdownOpen(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg ${
                        filterView === view ? 'text-orange-700 font-semibold' : 'text-gray-700'
                      }`}
                    >
                      {filterLabels[view]}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        }
        toolbarActions={
          <button
            onClick={handleOpenWizardNew}
            className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-white bg-orange-600 rounded-lg hover:bg-orange-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            New Dataset
          </button>
        }
        emptyIcon={Layers}
        emptyTitle="No dataset templates"
        emptySubtitle='Click "New Dataset" to create your first migration dataset template.'
        panelHeader={selectedTemplate ? (
          <DatasetDetailSlideInHeader
            template={selectedTemplate}
            onActivate={(id) => void handleActivate(id)}
            onDeactivate={(id) => void handleDeactivate(id)}
            onClose={() => setSelectedId(null)}
            isMutating={isMutating}
          />
        ) : undefined}
        onPanelClose={() => setSelectedId(null)}
        panelContent={selectedTemplate ? (
          <DatasetDetailSlideIn template={selectedTemplate} />
        ) : null}
      />

      {/* Dataset Builder Wizard */}
      {wizardOpen && (
        <DatasetBuilderWizard
          editId={wizardEditId}
          onClose={handleWizardClose}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <DeleteConfirmModal
          template={deleteTarget}
          onConfirm={() => void handleDeleteConfirm()}
          onCancel={() => setDeleteTarget(null)}
          isMutating={isMutating}
        />
      )}
    </div>
  );
}
